import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Search, Download, Printer, ExternalLink, FileText, Mail, Loader2, Calendar,
  CheckCircle2, AlertCircle, Clock, RotateCcw, FileDown, X, Store, Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

const RANGE_OPTIONS = [
  { id: "today", label: "Heute" },
  { id: "week", label: "Diese Woche" },
  { id: "month", label: "Dieser Monat" },
  { id: "all", label: "Alle" },
  { id: "custom", label: "Zeitraum…" },
];

const STATUS_COLORS = {
  paid:     "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  pending:  "bg-amber-500/10 text-amber-400 border-amber-500/30",
  failed:   "bg-red-500/10 text-red-400 border-red-500/30",
  refunded: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  initiated:"bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

const STATUS_ICONS = {
  paid:     CheckCircle2,
  pending:  Clock,
  failed:   AlertCircle,
  refunded: RotateCcw,
  initiated:Clock,
};

function fmtEUR(v) {
  return new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v || 0);
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("de-AT", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
  } catch { return iso; }
}

export default function AdminOrders() {
  const [range, setRange] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, revenue: 0, tax: 0, stripe_dashboard: "" });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null); // order_id currently performing action

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range !== "all") params.range = range;
      if (range === "custom") { if (start) params.start = start; if (end) params.end = end; }
      if (status && status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/orders", { params });
      setData(data);
    } catch (e) {
      toast.error("Bestellungen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [range, start, end, status, q]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleResend = async (order) => {
    if (!order.stripe_invoice_id) {
      toast.error("Diese Bestellung hat noch keine Stripe-Rechnung");
      return;
    }
    setBusy(order.id);
    try {
      await api.post(`/admin/orders/${order.id}/resend-invoice`);
      toast.success("Rechnung erneut versendet ✉️");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versenden fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  const handlePrint = (order) => {
    if (order.stripe_invoice_pdf) {
      window.open(order.stripe_invoice_pdf, "_blank");
    } else if (order.stripe_hosted_invoice_url) {
      window.open(order.stripe_hosted_invoice_url, "_blank");
    } else {
      printOrder(order);
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (range !== "all") params.set("range", range);
    if (range === "custom") { if (start) params.set("start", start); if (end) params.set("end", end); }
    if (status && status !== "all") params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    // Use token-aware download
    const url = `${api.defaults.baseURL}/admin/orders/export.csv?${params.toString()}`;
    const token = localStorage.getItem("hdh_token");
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `hdh-bestellungen-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("CSV heruntergeladen");
      })
      .catch(() => toast.error("CSV-Export fehlgeschlagen"));
  };

  const items = data.items || [];
  const dashboardBase = data.stripe_dashboard || "https://dashboard.stripe.com";

  return (
    <div data-testid="admin-orders" className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Verkauf · Stripe</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-1">Bestellungen & Rechnungen</h1>
          <p className="text-sm text-zinc-500 mt-1">Alle Stripe-Zahlungen mit Rechnung, Beleg & Steuerdaten — gebündelt im Haus.</p>
        </div>
        <Button onClick={exportCsv} variant="outline" className="bg-white/5 border-white/15 text-white hover:bg-white/10" data-testid="admin-orders-export-csv">
          <FileDown className="w-4 h-4 mr-1.5" /> CSV Export
        </Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Bestellungen" value={data.total ?? 0} />
        <Stat label="Brutto-Umsatz" value={fmtEUR(data.revenue)} />
        <Stat label="USt 20 %" value={fmtEUR(data.tax)} />
        <Stat label="Ø Bestellwert" value={data.total ? fmtEUR(data.revenue / data.total) : fmtEUR(0)} />
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl border border-white/10 bg-[#121215] p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 mr-1">
          <Calendar className="w-4 h-4 text-zinc-500" />
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Zeitraum</span>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="bg-[#0F0F12] border-white/10 text-white w-[150px] h-9" data-testid="orders-range-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#15151A] border-white/10 text-white">
            {RANGE_OPTIONS.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>

        {range === "custom" && (
          <>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="bg-[#0F0F12] border-white/10 text-white w-[170px] h-9" data-testid="orders-start-date" />
            <span className="text-zinc-500 text-sm">–</span>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              className="bg-[#0F0F12] border-white/10 text-white w-[170px] h-9" data-testid="orders-end-date" />
          </>
        )}

        <div className="h-6 w-px bg-white/10 mx-1"></div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="bg-[#0F0F12] border-white/10 text-white w-[140px] h-9" data-testid="orders-status-select">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#15151A] border-white/10 text-white">
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="paid">Bezahlt</SelectItem>
            <SelectItem value="pending">Ausstehend</SelectItem>
            <SelectItem value="failed">Fehlgeschlagen</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Name, Email, BestellNr…"
            className="bg-[#0F0F12] border-white/10 text-white pl-9 h-9"
            data-testid="orders-search"
          />
        </div>

        {loading && <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />}
      </div>

      {/* Table */}
      <div className="bg-[#121215] rounded-2xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Datum</th>
                <th className="text-left px-4 py-3">BestellNr</th>
                <th className="text-left px-4 py-3">Kunde</th>
                <th className="text-left px-4 py-3">Produkte</th>
                <th className="text-left px-4 py-3">Versand</th>
                <th className="text-right px-4 py-3">USt</th>
                <th className="text-right px-4 py-3">Brutto</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3 w-[240px]">Aktionen</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-white/5">
              {items.map((o) => {
                const StatusIcon = STATUS_ICONS[o.payment_status] || Clock;
                const total = o.amount_total ?? o.total ?? 0;
                const tax = o.tax_amount ?? o.tax ?? 0;
                const hasInvoice = !!o.stripe_invoice_id;
                const hasPdf = !!o.stripe_invoice_pdf;
                const piId = o.stripe_payment_intent_id;
                return (
                  <tr key={o.id} className="hover:bg-white/[0.03] transition-colors" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 text-zinc-400 whitespace-nowrap text-xs">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-3 text-zinc-200 font-mono text-xs">{o.internal_order_id || o.id.slice(0,8)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{o.customer_name || "—"}</div>
                      <div className="text-xs text-zinc-500">{o.email || "—"}</div>
                      {o.phone && <div className="text-xs text-zinc-600">{o.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      {(o.items || []).slice(0, 3).map((it, i) => (
                        <div key={i} className="text-xs whitespace-nowrap">{it.qty}× {it.name}</div>
                      ))}
                      {(o.items?.length || 0) > 3 && <div className="text-[10px] text-zinc-600">+{o.items.length - 3} weitere</div>}
                    </td>
                    <td className="px-4 py-3" data-testid={`order-shipping-${o.id}`}>
                      <ShippingBadge order={o} />
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-500 numeric text-xs">{fmtEUR(tax)}</td>
                    <td className="px-4 py-3 text-right font-display font-semibold text-white numeric">{fmtEUR(total)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${STATUS_COLORS[o.payment_status] || STATUS_COLORS.initiated}`}>
                        <StatusIcon className="w-3 h-3" />
                        {o.payment_status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn title="Details ansehen" onClick={() => setDetail(o)} testId={`order-view-${o.id}`}>
                          <FileText className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn
                          title={hasPdf ? "PDF herunterladen" : "Keine Rechnung"}
                          disabled={!hasPdf}
                          onClick={() => window.open(o.stripe_invoice_pdf, "_blank")}
                          testId={`order-pdf-${o.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn
                          title="Drucken / Rechnung öffnen"
                          onClick={() => handlePrint(o)}
                          testId={`order-print-${o.id}`}
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn
                          title="In Stripe öffnen"
                          disabled={!piId}
                          onClick={() => window.open(`${dashboardBase}/payments/${piId}`, "_blank")}
                          testId={`order-stripe-${o.id}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn
                          title={hasInvoice ? "Rechnung erneut senden" : "Keine Rechnung vorhanden"}
                          disabled={!hasInvoice || busy === o.id}
                          onClick={() => handleResend(o)}
                          testId={`order-resend-${o.id}`}
                        >
                          {busy === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && !loading && (
                <tr><td colSpan={9} className="text-center py-16 text-zinc-500">Keine Bestellungen im Zeitraum</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail dialog */}
      <OrderDetailDialog order={detail} dashboardBase={dashboardBase} onClose={() => setDetail(null)} onResend={handleResend} busy={busy} />
    </div>
  );
}

function ShippingBadge({ order }) {
  const isPickup = order.shipping_id === "pickup" || /abholung/i.test(order.shipping_method || "");
  const country = order.shipping_country || "";
  if (isPickup) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold border bg-amber-500/10 border-amber-500/30 text-amber-300 whitespace-nowrap">
        <Store className="w-3 h-3" /> Abholung im Shop
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold border bg-sky-500/10 border-sky-500/30 text-sky-300 whitespace-nowrap">
      <Truck className="w-3 h-3" /> Lieferung{country ? ` · ${country}` : ""}
    </span>
  );
}

function Stat({ label, value }) {  return (
    <div className="rounded-2xl border border-white/10 bg-[#121215] p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold text-white numeric mt-1">{value}</div>
    </div>
  );
}

function IconBtn({ children, title, onClick, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testId}
      className={`w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center transition-colors ${
        disabled ? "opacity-30 cursor-not-allowed" : "text-zinc-300 hover:text-white hover:bg-white/10"
      }`}
    >
      {children}
    </button>
  );
}

function OrderDetailDialog({ order, dashboardBase, onClose, onResend, busy }) {
  if (!order) return null;
  const total = order.amount_total ?? order.total ?? 0;
  const tax = order.tax_amount ?? order.tax ?? 0;
  const subtotal = order.subtotal ?? (total - (order.shipping_fee ?? 0) - tax);
  const piId = order.stripe_payment_intent_id;

  return (
    <Dialog open={!!order} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0F0F12] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center justify-between gap-3">
            Bestellung {order.internal_order_id || order.id.slice(0, 8)}
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[order.payment_status] || STATUS_COLORS.initiated}`}>
              {order.payment_status}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Datum">{fmtDate(order.created_at)}</Field>
            <Field label="Zahlungsart">{order.payment_method || "stripe"}</Field>
            <Field label="Kunde">{order.customer_name || "—"}</Field>
            <Field label="Email">{order.email || "—"}</Field>
            <Field label="Telefon">{order.phone || "—"}</Field>
            <Field label="Adresse">{order.address || "—"}</Field>
            <Field label="Versandart">
              <span data-testid="order-detail-shipping"><ShippingBadge order={order} /></span>
            </Field>
            <Field label="Lieferland">{order.shipping_country || "—"}</Field>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold mb-2">Positionen</div>
            <div className="rounded-xl border border-white/10 divide-y divide-white/5">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex justify-between px-3 py-2 text-sm">
                  <span className="text-zinc-300">{it.qty}× {it.name}</span>
                  <span className="font-mono text-zinc-400 numeric">{fmtEUR((it.price || 0) * (it.qty || 1))}</span>
                </div>
              ))}
              <div className="px-3 py-2 text-sm flex justify-between text-zinc-500">
                <span>Zwischensumme</span><span className="numeric">{fmtEUR(subtotal)}</span>
              </div>
              {(order.shipping_fee ?? 0) > 0 ? (
                <div className="px-3 py-2 text-sm flex justify-between text-zinc-500">
                  <span>Versand{order.shipping_method ? ` · ${order.shipping_method}` : ""}</span>
                  <span className="numeric">{fmtEUR(order.shipping_fee)}</span>
                </div>
              ) : (
                <div className="px-3 py-2 text-sm flex justify-between text-zinc-500">
                  <span>{order.shipping_id === "pickup" ? "Abholung im Shop" : "Versand"}</span>
                  <span className="text-emerald-400">Gratis</span>
                </div>
              )}
              <div className="px-3 py-2 text-sm flex justify-between text-zinc-500">
                <span>USt 20 %</span><span className="numeric">{fmtEUR(tax)}</span>
              </div>
              <div className="px-3 py-2 text-base font-semibold flex justify-between text-white">
                <span>Brutto</span><span className="numeric font-display">{fmtEUR(total)}</span>
              </div>
            </div>
          </div>

          {/* Stripe IDs */}
          <div className="rounded-xl border border-white/10 p-3 space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF2D40] font-semibold">Stripe-Referenzen</div>
            <IdRow label="Checkout Session" value={order.stripe_checkout_session_id} />
            <IdRow label="Payment Intent" value={piId} />
            <IdRow label="Invoice ID" value={order.stripe_invoice_id} />
            <IdRow label="Customer ID" value={order.stripe_customer_id} />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            <ActionBtn
              icon={<FileText className="w-3.5 h-3.5" />}
              label="Rechnung ansehen"
              disabled={!order.stripe_hosted_invoice_url}
              onClick={() => window.open(order.stripe_hosted_invoice_url, "_blank")}
            />
            <ActionBtn
              icon={<Download className="w-3.5 h-3.5" />}
              label="PDF herunterladen"
              disabled={!order.stripe_invoice_pdf}
              onClick={() => window.open(order.stripe_invoice_pdf, "_blank")}
            />
            <ActionBtn
              icon={<Printer className="w-3.5 h-3.5" />}
              label="Drucken"
              onClick={() => {
                const url = order.stripe_invoice_pdf || order.stripe_hosted_invoice_url;
                if (url) window.open(url, "_blank");
                else printOrder(order);
              }}
            />
            <ActionBtn
              icon={<ExternalLink className="w-3.5 h-3.5" />}
              label="In Stripe öffnen"
              disabled={!piId}
              onClick={() => window.open(`${dashboardBase}/payments/${piId}`, "_blank")}
            />
            <ActionBtn
              icon={busy === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
              label="An Kunden senden"
              disabled={!order.stripe_invoice_id || busy === order.id}
              onClick={() => onResend(order)}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{label}</div>
      <div className="text-sm text-white mt-0.5">{children}</div>
    </div>
  );
}

function IdRow({ label, value }) {
  if (!value) return (
    <div className="flex justify-between gap-3 text-xs"><span className="text-zinc-500">{label}</span><span className="text-zinc-600">—</span></div>
  );
  return (
    <div className="flex justify-between gap-3 text-xs items-center">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <button
        className="font-mono text-zinc-300 truncate text-right hover:text-white max-w-[60%]"
        title="Klick um zu kopieren"
        onClick={() => { navigator.clipboard.writeText(value); toast.success("Kopiert"); }}
      >{value}</button>
    </div>
  );
}

function ActionBtn({ icon, label, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-xs font-medium transition-colors ${
        disabled
          ? "opacity-40 cursor-not-allowed border-white/10 text-zinc-500"
          : "border-white/15 text-white hover:bg-white/10"
      }`}
    >
      {icon}{label}
    </button>
  );
}

/** Fallback printable view if no Stripe PDF is yet available */
function printOrder(order) {
  const w = window.open("", "_blank");
  if (!w) return;
  const items = (order.items || []).map(it => `<tr><td>${it.qty}× ${it.name}</td><td style="text-align:right">€${((it.price||0)*(it.qty||1)).toFixed(2)}</td></tr>`).join("");
  const total = (order.amount_total ?? order.total ?? 0).toFixed(2);
  const tax = (order.tax_amount ?? order.tax ?? 0).toFixed(2);
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${order.internal_order_id || order.id}</title>
    <style>body{font-family:system-ui;max-width:680px;margin:32px auto;padding:0 16px;color:#111}h1{font-size:24px;margin:0}small{color:#666}table{width:100%;border-collapse:collapse;margin:16px 0}td{padding:6px 0;border-bottom:1px solid #eee}.total{font-weight:600;font-size:18px}</style>
    </head><body>
    <h1>Haus des Handys — Bestellung ${order.internal_order_id || order.id}</h1>
    <small>Eljhat Kadrii e.U. · FN 628945w · UID ATU81376119 · Hernalser Hauptstraße 135, 1170 Wien</small>
    <hr style="margin:16px 0">
    <p><strong>${order.customer_name||""}</strong><br>${order.email||""}<br>${order.phone||""}<br>${order.address||""}</p>
    <table>${items}<tr><td>USt 20%</td><td style="text-align:right">€${tax}</td></tr><tr class="total"><td>Brutto</td><td style="text-align:right">€${total}</td></tr></table>
    <script>window.print()</script>
    </body></html>`);
  w.document.close();
}
